/**
 * Shared GraphQL fragments to keep queries feeding the AI Findings surfaces
 * (StepFindingsBadge, AiFlagsPill, LicenseInformationCard) selecting the
 * same fields across every parent query.
 *
 * ENG-4328 revealed two variants of the same class of bug — different
 * queries selecting different subsets of `DocumentPrediction`, causing
 * `bestPredictionsByField` and the rule engine to see different data on
 * different surfaces. Consolidating the selection into these fragments
 * turns the drift-prone copy-paste of field lists into a single source of
 * truth: adding a field to a fragment updates every consumer at once, and
 * missing a field is a compile-time error in tests rather than a silent
 * production divergence.
 *
 * Not every query needs the full set — feel free to inline a smaller
 * selection for one-off use cases (e.g. an admin table that only needs
 * `field` and `confidence`). The fragments here are specifically for the
 * queries that pass data into the shared AI rule engine.
 */

/**
 * The DocumentPrediction fields needed by the CDL / DOT Medical rule engine
 * (`bestPredictionsByField`, `cdlHasValue`, `medicalHasValue`, and the
 * comparison rules in `aiFlagsRules.ts`).
 *
 * `textValue` powers License # / State / Class / Endorsements comparisons —
 * dropping it silently under-reports on those fields (ENG-4328). `autoApplied`
 * is used to filter already-decided predictions. `categoryValue` powers the
 * DOT Medical Category comparison.
 */
export const AI_PREDICTION_FIELDS_FRAGMENT = gql`
  fragment AiPredictionFields on DocumentPrediction {
    field
    confidence
    categoryValue
    expirationValue
    textValue
    reviewDecision
    autoApplied
  }
`

/**
 * The AiExtractedRow shape used by MVR incident counting on the checklist +
 * sidebar (`StepFindingsBadge`'s `getMvrBadge`). Records are used only to
 * verify "this row is a count row, not a scalar" — the badge just totals the
 * `value` column across count rows.
 */
export const AI_EXTRACTED_ROW_FIELDS_FRAGMENT = gql`
  fragment AiExtractedRowFields on AiExtractedRow {
    field
    label
    value
    records {
      date
    }
  }
`
